import type { RootState } from 'app/store';
import { usePreviousValue } from 'hooks/usePrevious';
import type { AnyObject } from 'types/AnyObject';

import type { RowClassParams, RowClassRules } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';

type HighLightRulesProps = {
    requestIds: string[];
    dataKeyAccessor: string;
    refreshKey: number | string | boolean;
    baseRules?: RowClassRules;
    gridApiRef: AgGridReact<any> | null | undefined;
};

const getDataKey = (dataObj: AnyObject) => Object.keys(dataObj ?? {})?.[0];

export const useHighLightRulesWithRequestData = ({
    requestIds = [],
    dataKeyAccessor,
    baseRules = {},
    gridApiRef,
    refreshKey,
}: HighLightRulesProps) => {
    const [savedIds, setSavedIds] = useState<number[]>([]);
    const prevRefreshKeyValue = usePreviousValue(refreshKey);

    useEffect(() => {
        if (prevRefreshKeyValue !== refreshKey) {
            setSavedIds([]);
        }
    }, [prevRefreshKeyValue, refreshKey]);

    const state = useSelector((s: RootState) => R.pick(requestIds, s.requests), shallowEqual);

    useEffect(() => {
        requestIds.forEach((requestId) => {
            const key = getDataKey(state[requestId]?.data);

            const requestData = state[requestId]?.data?.[key]?.data;

            if (Array.isArray(requestData)) {
                setSavedIds((p) => R.union(p, requestData));
            }

            if (requestData?.[dataKeyAccessor]) {
                setSavedIds((p) => R.union(p, requestData[dataKeyAccessor]));
            }
        });
    }, [dataKeyAccessor, requestIds, state]);

    useEffect(() => {
        gridApiRef?.api?.forEachNode?.((node) => {
            node.updateData(node.data);
        });
    }, [gridApiRef?.api, savedIds]);

    const rowClickHandler = useCallback((rowId: number) => {
        setSavedIds((prevState) => {
            if (prevState.includes(rowId)) {
                return prevState.filter((r) => r !== rowId);
            }

            return prevState;
        });
    }, []);

    const rules = useMemo(
        () => ({
            ...baseRules,
            highlightedRowClass: (rowData: RowClassParams) => savedIds.includes(rowData?.data?.id),
        }),
        [baseRules, savedIds],
    );

    const getTableDataId = useCallback(({ data }: { data: { id: any } }) => data?.id, []);

    return useMemo(
        () => ({
            rules,
            rowClickHandler,
            getTableDataId,
        }),
        [rowClickHandler, rules, getTableDataId],
    );
};
